<template>
	<v-row dense class="ma-0">
		<v-col cols="12" class="pa-0">
			<v-data-table
				class="medical-staff-data-table"
				:no-data-text="$t('no-data-available')"
				:items="filteredMedicalTeams"
				:headers-length="4"
				fixed-header
				:loading="loading"
				show-expand
				dense
				hide-default-header
				:height="pageHeight(174)"
				ref="table"
				:footer-props="getDataTableFooterProps('medicalTeamTable')"
				:items-per-page="-1">
				<template #item="{ item, expand, isExpanded, index }">
					<tr
						@click="expand(!isExpanded)"
						class="pa-2 text-left"
						:class="{ 'odd-row': index % 2, 'expanded-row': isExpanded }">
						<td>
							<v-icon small color="primary">
								{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
							</v-icon>
							<span class="table-font-size">{{ item.patientFullname }}</span>
						</td>

						<td>
							<v-chip pill x-small color="primary" class="ma-2 text--black custom-chip-size" label>
								{{ item.membersList.length }} {{ item.membersList.length > 1 ? $t('members') : $t('member') }}
							</v-chip>
						</td>

						<td>
							<img class="mr-1" height="18px" :src="patient_black" />
							<span class="table-font-size">{{ item.patientFullname }}</span>
						</td>

						<td class="text-right">
							<v-tooltip left color="primary">
								<template #activator="{ on }">
									<v-icon
										size="18"
										@click.stop="navigateToPatientDetails(item.patientId)"
										color="main_black"
										v-on="on"
										class="mr-2">
										mdi-open-in-new
									</v-icon>
								</template>
								<span>{{ $t('show-patient-details') }}</span>
							</v-tooltip>

							<v-tooltip left color="primary">
								<template #activator="{ on }">
									<v-icon size="18" @click.stop="synchronizeDrawers(item)" color="main_black" v-on="on">
										mdi-pencil
									</v-icon>
								</template>
								<span>{{ $t('edit') }}</span>
							</v-tooltip>
						</td>
					</tr>
				</template>

				<template #expanded-item="{ item }">
					<td :colspan="headers.length" class="pa-3">
						<v-data-table
							class="data-table-outline"
							:no-data-text="$t('no-data-available')"
							:items="item.membersList"
							:headers="headers"
							fixed-header
							dense
							hide-default-footer>
							<template #item="{ item }">
								<tr>
									<td class="text-left pl-2 color-black billings-border-bottom font-size12-5">
										<span class="font-weight-medium text-left font-size12-5">
											{{ item.firstName }} {{ item.lastName }}
										</span>
									</td>
									<td class="table-font-size billings-border-bottom">
										<img v-if="item.roleId === 4" :src="getNurseIcon(item.status)" height="17px" />
										<img v-else-if="item.roleId === 3" :src="getDoctorIcon(item.status)" height="17px" />
										{{ item.roleId === 3 ? $t('doctor') : $t('nurse') }}
									</td>
									<td class="table-font-size billings-border-bottom">
										<v-icon x-small color="main_black" class="mr-1">mdi-email</v-icon>{{ item.email }}
									</td>
									<td class="table-font-size billings-border-bottom">
										<v-icon x-small color="main_black" class="mr-1">mdi-phone</v-icon>{{ item.phone }}
									</td>
									<td class="table-font-size billings-border-bottom">
										{{ item.hospitalName }}
									</td>
									<td class="billings-border-bottom">
										<span v-if="item.isPrimaryDoctor">
											<v-icon color="black" class="mdi mdi-check-circle pr-6 mdi-18px float-right"></v-icon>
										</span>
									</td>
								</tr>
							</template>
						</v-data-table>
					</td>
				</template>
			</v-data-table>
		</v-col>
	</v-row>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { patientBlack } from '@/assets';
import { getDataTableFooterProps, getDoctorIcon, getNurseIcon } from '@/utils/utilities';
export default {
	data () {
		return {
			loading: true,
			headers: [
				{ text: this.$t('name'), sortable: false, class: 'color_header', align: 'left', value: 'patientFullname' },
				{ text: this.$t('header-role'), sortable: false, class: 'color_header', value: 'firstName' },
				{ text: this.$t('header-email'), sortable: false, class: 'color_header' },
				{ text: this.$t('header-phone'), sortable: false, class: 'color_header' },
				{ text: this.$t('header-hospital-name'), sortable: false, class: 'color_header' },
				{ text: this.$t('primary-doctor'), sortable: false, class: 'color_header', align: 'end' },
			],
			patient_black: patientBlack,
		};
	},
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			medicalTeams: (state) => state.medicalteam.medicalTeam,
		}),
		...mapGetters({
			getUser: 'authentication/getUser',
			pageHeight: 'pageHeight',
			appBarSearch: 'filterbar/getSearch',
		}),

		computedMedicalTeams () {
			const teamsWithPrimaryDocFlag = this.medicalTeams.map((team) => {
				const newTeam = { ...team };

				newTeam.membersList = team.membersList.map((member) => ({
					...member,
					hospitalName: team.hospitalName,
					isPrimaryDoctor: member.id === team.primaryDoctorId,
				}));

				return newTeam;
			});
			return teamsWithPrimaryDocFlag;
		},
		filteredMedicalTeams () {
			if (!this.appBarSearch) {
				return this.computedMedicalTeams;
			}
			const searchTerm = this.appBarSearch.toLowerCase();

			return this.computedMedicalTeams.reduce((result, team) => {
				if (team.patientFullname.toLowerCase().includes(searchTerm)) {
					result.push(team);
					return result;
				}
				const matchingMembers = team.membersList.filter((member) => {
					return (
						(member.firstName + ' ' + member.lastName).toLowerCase().includes(searchTerm) ||
						member.email.toLowerCase().includes(searchTerm) ||
						member.phone.toLowerCase().includes(searchTerm) ||
						member.hospitalName.toLowerCase().includes(searchTerm)
					);
				});
				if (matchingMembers.length > 0) {
					result.push({
						...team,
						membersList: matchingMembers,
					});
				}
				return result;
			}, []);
		},
	},

	async mounted () {
		await this.getMedicalTeams();
	},
	methods: {
		getNurseIcon,
		getDoctorIcon,
		getDataTableFooterProps,

		navigateToPatientDetails (patientId) {
			this.$router.push({ name: 'Patient Details', params: { id: patientId } });
		},

		async synchronizeDrawers (item) {
			const currentHospital = localStorage.getItem('selectedHospitalAssignedId');
			await this.$store.dispatch('medicalteam/getMedicalTeamForPatient', item.patientId).then(() => {
				this.$store.dispatch('hospitals/getRelatedMedicalStaffForHospital', currentHospital);
				this.$store.commit('SET_UPDATE_DETAILS_DRAWER', { item, updateDrawer: true, page2: 'medicalteam' });
				this.loading = false;
			});
		},

		async getMedicalTeams () {
			this.loading = true;
			await this.$store.dispatch('medicalteam/getMedicalTeams', this.getUser.hospitalAssignedData.id).then((res) => {
				this.loading = false;
			});
		},
	},
};
</script>
